/**
 * Dates and times — docs/24 §8. **One module owns the format.** Nothing else in
 * this console calls `toLocaleString` on a date.
 *
 * Failure mode without that rule: two grid helpers say one thing and eight
 * screens formatting timestamps inline say another. Nobody owns the format, so
 * nobody notices the clock disagreeing with itself across the console.
 *
 * The format is `YYYY/MM/DD HH:mm` in BOTH locales, and it is built by
 * arithmetic rather than asked of `Intl`:
 *
 *  - Chromium has no full ICU date-time data for `mn-MN`, so
 *    `toLocaleString('mn-MN')` silently falls back — and the fallback puts
 *    `9/7/2026, 1:46:35 PM` into a Mongolian console: month-first AND a 12-hour
 *    clock, neither of which anyone chose, both of which look like a decision
 *    until you go looking for where it was made.
 *  - Asking `Intl` for `2-digit` parts fixes the padding and leaves the ORDER to
 *    the same fallback, so `09/07/2026` still reads as 9 July to half the people
 *    looking at it.
 *  - `getHours()` gives midnight as `00` for nothing, where going through `Intl`
 *    needs `hour12: false` AND `hourCycle: 'h23'`, because the first alone can
 *    yield `24:00`, which reads as tomorrow.
 *
 * The format does not change with the UI language. An `en` operator and an `mn`
 * operator reading the same settlement run must read the same date, and
 * year-first is the only order that sorts as text — which matters the moment one
 * of these lands in an exported spreadsheet (§6.2).
 */

/** Absent is an em dash, never a blank. */
export const ABSENT = "—";

function pad(value: number, width = 2): string {
  return String(value).padStart(width, "0");
}

function parse(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * `YYYY/MM/DD`.
 *
 * A blank cell says «this failed to load»; `—` says «there is no date». On a
 * register where half the rows legitimately have none — an unshipped order, an
 * unreconciled payout — that distinction is the column's whole content.
 */
export function asDate(value: string | Date | null | undefined): string {
  const date = parse(value);
  if (!date) return ABSENT;
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
}

/**
 * `YYYY-MM-DD` — an operating day as routing and fulfilment write it, not as a
 * reader reads it.
 *
 * Deliberately a different function from {@link asDate} and deliberately
 * hyphenated: this string goes ON THE WIRE, as `planDate`, and the display
 * format is slashed. One function serving both would mean a change to how the
 * console draws a date silently changing what it asks the api for.
 *
 * Built from the browser's LOCAL parts, because that is what the operator means
 * by «today» and the console runs in Ulaanbaatar. `toISOString()` would answer
 * in UTC, which is yesterday for the first eight hours of every Mongolian day —
 * so a dispatcher planning at 07:00 would plan the previous day's manifest.
 */
export function asPlanDate(value: Date = new Date()): string {
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

/** `HH:mm`, 24-hour, midnight as `00:00`. */
export function asTime(value: string | Date | null | undefined): string {
  const date = parse(value);
  if (!date) return ABSENT;
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `YYYY/MM/DD HH:mm`. */
export function asDateTime(value: string | Date | null | undefined): string {
  const date = parse(value);
  if (!date) return ABSENT;
  return `${asDate(date)} ${asTime(date)}`;
}

/**
 * A filename stamp: `YYYYMMDD-HHmm`, sortable and safe on every filesystem.
 * Used by the export verbs (§6.2), where the moment is part of the name.
 */
export function asStamp(value: Date = new Date()): string {
  return (
    `${value.getFullYear()}${pad(value.getMonth() + 1)}${pad(value.getDate())}` +
    `-${pad(value.getHours())}${pad(value.getMinutes())}`
  );
}

/**
 * A rate is not money (§8a). Commission percentages, VAT at 10 % and buffer
 * percentages are their own format, and the value shown is the one IN FORCE at
 * the moment of the record — never today's.
 */
export function asRate(basisPoints: number | null | undefined): string {
  if (basisPoints === null || basisPoints === undefined || Number.isNaN(basisPoints)) return ABSENT;
  const percent = basisPoints / 100;
  // Two decimals only when they carry something: 15 % is «15%», 12.5 % is «12.5%».
  const text = Number.isInteger(percent) ? String(percent) : percent.toFixed(2).replace(/0+$/, "");
  return `${text}%`;
}

/**
 * A span of seconds as `4ц 05м` / `4h 05m`.
 *
 * The unit letters are the caller's, because they are words and words are
 * translated; this module owns the ARITHMETIC and the padding. A driving time
 * of four and a quarter hours rendered as `15300` is a number nobody converts
 * in their head, and rendered as `4.25` is a decimal hour, which is a payroll
 * unit and not a clock.
 *
 * Minutes are zero-padded so a column of them lines up under `tabular`; hours
 * are not, because a leading zero on the hour reads as a clock time.
 */
export function asDuration(seconds: number | null | undefined, units: { hour: string; minute: string }): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return ABSENT;
  const whole = Math.max(0, Math.round(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  if (hours === 0) return `${minutes}${units.minute}`;
  return `${hours}${units.hour} ${pad(minutes)}${units.minute}`;
}

/**
 * A rule's lookback window — seconds, as days and hours.
 *
 * Separate from `asDuration` because it is a different reading, not a longer
 * one. A driving time is a clock span and «4ц 05м» is how a dispatcher says it;
 * a velocity window is «30 хоног», and the same arithmetic through `asDuration`
 * renders that as `720ц 00м`, which is true and which nobody converts back into
 * a month. The risk desk's windows run from 15 minutes to 30 days, so both ends
 * of that range have to read.
 *
 * Trailing zero parts are dropped — «1 хоног» rather than «1 хоног 0ц 0м» — but
 * a window that is genuinely zero renders as zero rather than as an empty
 * string, because a counter with no window is a rule that never fires and the
 * operator has to be able to see it.
 */
export function asWindow(
  seconds: number | null | undefined,
  units: { day: string; hour: string; minute: string },
): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return ABSENT;
  const whole = Math.max(0, Math.round(seconds));
  const days = Math.floor(whole / 86_400);
  const hours = Math.floor((whole % 86_400) / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}${units.day}`);
  if (hours > 0) parts.push(`${hours}${units.hour}`);
  if (minutes > 0) parts.push(`${minutes}${units.minute}`);
  return parts.length === 0 ? `0${units.minute}` : parts.join(" ");
}

/**
 * Metres as kilometres to one place.
 *
 * `toFixed` rather than `Intl.NumberFormat`, which §8a reserves for money: this
 * is a distance, it takes no currency and no grouping, and one decimal is the
 * precision a route's length is actually known to. The separator is a dot in
 * both languages for the same reason the date is year-first in both — a figure
 * that changes shape with the UI language cannot be compared across two screens.
 */
export function asKilometres(metres: number | null | undefined, unit: string): string {
  if (metres === null || metres === undefined || !Number.isFinite(metres)) return ABSENT;
  return `${(metres / 1000).toFixed(1)}${unit}`;
}

/**
 * A `YYYY-MM-DD` from a date input, as the LAST instant of that day, locally.
 *
 * Every «until» an operator types in this console is a calendar day and every
 * api field behind one is an instant. Sending midnight would expire a block, a
 * gift card or a listing at the START of the day the operator typed — a day
 * early, and an off-by-one nobody can see on the screen that caused it.
 *
 * Local rather than UTC because the day the operator means is their own: 23:59
 * on the 9th in Ulaanbaatar is 15:59 UTC, and a UTC end-of-day would keep a
 * block alive eight hours into the 10th.
 */
export function endOfLocalDay(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  if (!year || !month || !date) return "";
  return new Date(year, month - 1, date, 23, 59, 59, 999).toISOString();
}

/**
 * A value rendered from an enum gets its first letter capitalised rather than
 * shown raw (§4.3 — sentence case for all UI text, in both languages). This is
 * the fallback for a code with no translated word; a status that a reader acts
 * on always has one (see `src/components/status.tsx`).
 */
export function asSentence(code: string): string {
  const words = code.toLowerCase().replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
